import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Input, Button, Form, Tooltip, Drawer, TreeSelect, Select, Tag, Spin, message, Switch } from 'antd';
import { FilterOutlined, LoadingOutlined, ArrowRightOutlined, CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import * as Utilities from '../../Utilities';
import * as Styles from '../../../../Styles';
import FileConstants from '../../../../FileConstants';

import './SearchItem.css';
import RoomsList from "../../CustomerComponents/SwapSpace/RoomsList";
import DottedLoader from "../../DottedLoader";

const categoryStyle = Styles.categoryStyle;
const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
        color: "black",
      }}
      spin
    />
  );

const SpaceSearch = () => {
    const pageSize = 100;
    const [searchValue, setSearchValue] = useState('');
    const [filtersDrawer, setFiltersDrawer] = useState(false);
    const [spaceCategoriesData, setSpaceCategoriesData] = useState([]);
    const [spaceCategoryValue, setSpaceCategoryValue] = useState([]);
    const [modelStatus, setModelStatus] = useState(window.location.pathname === '/artist_search_item' ? [] : [5]);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [appliedFiltersList, setAppliedFiltersList] = useState([]);
    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({}), []);
    const [offset, setOffset] = useState(0);
    const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
    const [spaces, setSpaces] = useState([]);
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [isLoadingSpaces, setIsLoadingSpaces] = useState(false);
    const [selectedAdd, setSelectedAdd] = useState([]);
    const [selectedRemove, setSelectedRemove] = useState([]);
    const [selectedElementDetials, setSelectedElementDetials] = useState([]);
    const [actionLoader, setActionLoader] = useState(false);
    const [visibleToArtist, setVisibleToArtist] = useState(false);
    const artistSearchSpace = window.location.pathname === '/artist_search_item';
    const [isRestoringFilters, setIsRestoringFilters] = useState(true);
    const defaultModelStatus = artistSearchSpace ? [] : [5];
    const [initialStatusInUrl, setInitialStatusInUrl] = useState(null);
    const currentRequestRef = useRef(null);

    useEffect(() => {
        setIsLoadingSpaces(true);
        // capture whether status existed in the URL initially
        const params = new URLSearchParams(window.location.search);
        setInitialStatusInUrl(params.has('status'));
        setSpaceCategoryData();
    }, []);

    // After categories load, restore filters from URL (tab=space) then fetch using overrides
    useEffect(() => {
        if (spaceCategoriesData.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl !== 'space') {
            // Not our tab; don't alter URL, just do a normal initial fetch
            fetchSpaces(true);
            setIsRestoringFilters(false);
            return;
        }

        const searchFromUrl = params.get('search');
        const categoryFromUrl = params.get('category');
        const statusFromUrl = params.get('status');
        const visibleFromUrl = params.get('visible_to_artist');

        const updates = [];
        if (searchFromUrl) updates.push(() => setSearchValue(searchFromUrl));
        if (categoryFromUrl) updates.push(() => setSpaceCategoryValue(categoryFromUrl.split(',')));
        if (statusFromUrl) updates.push(() => setModelStatus(statusFromUrl.split(',').map(s => parseInt(s))));
        if (visibleFromUrl === 'true') updates.push(() => setVisibleToArtist(true));
        updates.forEach(u => u());

        const overrides = {};
        if (categoryFromUrl) overrides.spaceCategoryValue = categoryFromUrl.split(',');
        if (statusFromUrl) overrides.modelStatus = statusFromUrl.split(',').map(s => parseInt(s));
        if (visibleFromUrl === 'true') overrides.visibleToArtist = true;

        const initialSearch = searchFromUrl ? searchFromUrl : undefined;
        const fetchPromise = fetchSpaces(true, initialSearch, overrides);
        if (fetchPromise && fetchPromise.finally) {
            fetchPromise.finally(() => setIsRestoringFilters(false));
        } else {
            setIsRestoringFilters(false);
        }
    }, [spaceCategoriesData]);

    const saveFiltersToURL = () => {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl !== 'space') return;

        // Save search
        if (searchValue) params.set('search', searchValue); else params.delete('search');
        // Save category
        if (spaceCategoryValue.length > 0) params.set('category', spaceCategoryValue.join(',')); else params.delete('category');
        // Save status (avoid default unless initially present)
        if (modelStatus.length > 0) {
            if (!(Array.isArray(defaultModelStatus) && modelStatus.length === defaultModelStatus.length && modelStatus.every((v, i) => v === defaultModelStatus[i])) || initialStatusInUrl) {
                params.set('status', modelStatus.join(','));
            } else {
                params.delete('status');
            }
        } else {
            params.delete('status');
        }
        // Save visible
        if (visibleToArtist) params.set('visible_to_artist', 'true'); else params.delete('visible_to_artist');

        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }

    useEffect(() => {
        if (!isRestoringFilters) saveFiltersToURL();
    }, [spaceCategoryValue, modelStatus, visibleToArtist, isRestoringFilters, searchValue]);

    //display tags when this is clicked
    useEffect(() => {
        if (spaceCategoryValue.length > 0
            || (modelStatus.length > 0)
            || visibleToArtist) {
            setFiltersApplied(true);
            forceUpdate();
            setAppliedFiltersList(getAppliedFilters())
        } else {
            setFiltersApplied(false);
            setAppliedFiltersList([])
        }
    }, [spaceCategoryValue, modelStatus, visibleToArtist, isRestoringFilters])

    // Trigger API call when filters change
    useEffect(() => {
        if (isRestoringFilters) return;
        // Reset offset and spaces when filters change
        setOffset(0);
        setSpaces([]);
        setAllDataLoaded(false);
        // Fetch spaces with new filters
        
        // Add a small delay to batch rapid filter changes and prevent race conditions
        const timeoutId = setTimeout(() => {
            fetchSpaces(true);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [spaceCategoryValue, modelStatus, visibleToArtist])

    const getSpacePayload = (customOffset = null, customSearchValue = null, overrides = null) => {
        let payload = {
            "required_fields": ["room_id","room_name", "brand_id", "category", "dimensions", 
            "customer_username", "model_status","thumbnail" ,"last_modified_stamp", "variant_of",
            "is_store_item", "company_id", "style", "platform", "tags", "duplication_status", "created_on_stamp"
            , "artist_visible"]
        }
        let filterString = '(is_hidden__not=true&&company_id__notexact=1)'
        
        // Use window.location.pathname directly
        if (artistSearchSpace) {
            filterString += '&&(artist_visible__exact=true)';
        }

        const effectiveCategory = overrides && overrides.spaceCategoryValue !== undefined ? overrides.spaceCategoryValue : spaceCategoryValue;
        const effectiveModelStatus = overrides && overrides.modelStatus !== undefined ? overrides.modelStatus : modelStatus;
        const effectiveVisibleToArtist = overrides && overrides.visibleToArtist !== undefined ? overrides.visibleToArtist : visibleToArtist;

        // Add category filter if categories are selected
        if (effectiveCategory.length > 0) {
            const categoryConditions = effectiveCategory.map(category => `category__exact='${category.replace(/'/g, "''")}'`).join('||');
            filterString += `&&(${categoryConditions})`;
        }

        // Add model status filter if selected
        if (effectiveModelStatus.length > 0) {
            const statusConditions = effectiveModelStatus.map(status => `model_status__in=[${status}]`).join('||');
            filterString += `&&(${statusConditions})`;
        }

        // Add visible to artist filter if enabled
        if (effectiveVisibleToArtist) {
            filterString += '&&(artist_visible__exact=true)';
        }

        const effectiveSearchValue = customSearchValue !== null ? customSearchValue : searchValue;
        if (effectiveSearchValue) {
            const parts = effectiveSearchValue.replace(/[^a-zA-Z0-9' ]/g, " ").split(" ").filter(Boolean);
            if (parts.length > 0) {
                const nameSearchConditions = parts.map(part => `room_name__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `&&((${nameSearchConditions})`;
                const categorySearchConditions = parts.map(part => `category__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${categorySearchConditions})`;
                const brandSearchConditions = parts.map(part => `brand_id__ilike='%${part.replace(/'/g, "''")}%'`).join('||');
                filterString += `||(${brandSearchConditions})`;

                const idSearchParts = parts.filter(part => /^\d+$/.test(part));
                const idSearchConditions = idSearchParts.map(part => `room_id__exact='${part.replace(/'/g, "''")}'`).join('||');
                if (idSearchConditions) {
                    filterString += `||(${idSearchConditions})`;
                }
                filterString += ")";
            }
        }

        payload['filter_string'] = filterString
        payload['pagination_filters'] = {
            'limit': pageSize,
            'offset': customOffset !== null ? customOffset : offset
        }
        payload['order_by'] = 'last_modified_stamp desc'
        return payload;
    }

    const updateOffset = (offset) => {
        let newOffset = offset + pageSize;
        setOffset(newOffset);
    };

    const fetchSpaces = (isInitialLoad = false, customSearchValue = null, overrides = null) => {
        // Cancel any existing request
        if (currentRequestRef.current) {
            currentRequestRef.current.cancel();
        }
        
        // Create a new cancel token for this request
        const cancelTokenSource = axios.CancelToken.source();
        currentRequestRef.current = cancelTokenSource;
        
        if (isInitialLoad) {
            setIsLoadingSpaces(true);
            setOffset(0);
            let payload = getSpacePayload(0, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.LIST_ROOMS, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingSpaces(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        const spacesWithEntityType = res.data.map(item => ({ 
                            ...item, 
                            entity_type: 'room',
                            last_modified: item.last_modified_stamp
                        }));
                        setSpaces(spacesWithEntityType);
                        updateOffset(0);
                        setIsLoadingMoreData(false);
                        setIsLoadingSpaces(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching spaces:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingSpaces(false);
                });
        } else {
            setIsLoadingMoreData(true);
            let payload = getSpacePayload(undefined, customSearchValue, overrides);
            return axios.post(ENVIRONMENT.LIST_ROOMS, payload, {
                cancelToken: cancelTokenSource.token
            })
                .then(res => {
                    if (res.data) {
                        if (!res.data.length) {
                          setAllDataLoaded(true);
                          setIsLoadingMoreData(false);
                          setIsLoadingSpaces(false);
                          return;
                        }
                        setAllDataLoaded(false);
                        const spacesWithEntityType = res.data.map(item => ({ 
                            ...item, 
                            entity_type: 'space',
                            last_modified: item.last_modified_stamp
                        }));
                        const tempSpaces = [...spaces, ...spacesWithEntityType];
                        setSpaces(tempSpaces);
                        updateOffset(offset);
                        setIsLoadingMoreData(false);
                        setIsLoadingSpaces(false);
                    }
                })
                .catch(error => {
                    // Don't handle errors for cancelled requests
                    if (axios.isCancel(error)) {
                        console.log('Request cancelled');
                        return;
                    }
                    console.error('Error fetching spaces:', error);
                    setIsLoadingMoreData(false);
                    setIsLoadingSpaces(false);
                });
        }
    }

    const setSpaceCategoryData = () => {
        let payload = {
            'request_type': 'room_model'
        }
        axios.post(ENVIRONMENT.GET_CATEGORY_AND_PRICE, payload)
            .then(res => {
                let categories = []
                if (res.data) {
                    res.data.map((request) => {
                        if (!categories.includes(request.category)) {
                            categories.push(request.category);
                        }
                    })
                    categories = categories.filter(item => item !== '')
                    setSpaceCategoriesData(categories);
                }
            });
    }



    const changeSearchValue = (value) => {
        setSearchValue(value);
        console.log('Search value:', value);
    }

    const openFilters = () => {
        setFiltersDrawer(true);
    };

    const closeFiltersDrawer = () => {
        setFiltersDrawer(false);
    }

    const changeSpaceCategory = (value) => {
        setSpaceCategoryValue(value);
    }



    const changeModelStatus = (value) => {
        setModelStatus(value);
    }



    const getAppliedFilters = () => {
        let appliedFilters = [...spaceCategoryValue, ...modelStatus]
        if (visibleToArtist) {
            appliedFilters.push('visible_to_artist');
        }
        appliedFilters = appliedFilters.map(item => item.toString().toLowerCase())
        return appliedFilters;
    }

    const closeTags = (entity, type) => {
        //remove the tag when this is clicked from showing result section
        if (type == 'space_category') {
            let arr = spaceCategoryValue.filter(category => { return entity != category });
            changeSpaceCategory(arr)
        } else if (type == 'model_status') {
            let arr = modelStatus.filter(status => { return entity != status });
            setModelStatus(arr);
        } else if (type == 'visible_to_artist') {
            setVisibleToArtist(false);
        }
    }

    const resetFilters = () => {
        spaceCategoryValue.length > 0 && spaceCategoryValue.map((category) => {
            closeTags(category, 'space_category');
        });
        modelStatus.length > 0 && modelStatus.map((status) => {
            closeTags(status, 'model_status');
        })
        visibleToArtist && closeTags('visible_to_artist', 'visible_to_artist');
    };

    const getFilteredTags = () => {
        let filteredTags = <>
            {filtersApplied && <Col span={24} style={{ marginLeft: 10, marginTop: 20, textAlign: "left" }}>
                <h4 className='manrope f-14 black-55 italic'>Showing results for</h4>
                <div style={{ marginBottom: 20 }}>
                    {
                        spaceCategoryValue.length > 0 && spaceCategoryValue.map((category) => {
                            return <Tag onClose={() => closeTags(category, 'space_category')}
                                closable visible={true}
                                className='manrope f-12 black-55 tag-filter'>Category: <span className='manrope f-12 grey-77'>{category}</span></Tag>
                        })}

                    {
                        modelStatus.length > 0 && modelStatus.map((status) => {
                            return <Tag onClose={() => closeTags(status, 'model_status')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Model Status: <span className='manrope f-12 grey-77'>{FileConstants.MODEL_STATUS_STRING[status]}</span></Tag>
                        })}

                    {visibleToArtist && (
                        <Tag onClose={() => closeTags('visible_to_artist', 'visible_to_artist')} closable visible={true} className='manrope f-12 black-55 tag-filter'>Visible to Artist</Tag>
                    )}
                </div>
            </Col>}
        </>

        return filteredTags;
    }

    // Add onLoadMore handler
    const handleLoadMore = () => {
        if (!isLoadingMoreData && !allDataLoaded) {
            fetchSpaces();
        }
    };

    const handleSearchIconClick = () => {
        if(searchValue && searchValue.trim() !== '')
        {
            setOffset(0);
            setSpaces([]);
            setAllDataLoaded(false);
            fetchSpaces(true);
        }
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setOffset(0);
        setSpaces([]);
        setAllDataLoaded(false);
        // Remove search parameter from URL
        const params = new URLSearchParams(window.location.search);
        params.delete("search");
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
        fetchSpaces(true, '', { searchValue: '' }); // Pass empty string as search value
    };

    useEffect(() => {
        console.log(selectedAdd, selectedRemove)
    }, [selectedAdd, selectedRemove])

    const handleAddToArtistSearch = () => {
        setActionLoader(true);
        let payload = {
            "add_ids": selectedAdd,
            "remove_ids": selectedRemove,
            "entity": "space"
        }

        axios.post(ENVIRONMENT.UPDATE_ARTIST_VISIBLE, payload)
            .then(res => {
                console.log(res);
                setActionLoader(false);
                message.success('Spaces added to artist search');
                window.location.reload();
            })
            .catch(error => {
                console.error('Error adding to artist search:', error);
                setActionLoader(false);
            });
    }

    return (
        <>
        { isLoadingSpaces && offset == 0 ? 
            <div className="justify-in-center">
                <DottedLoader />
            </div>
        :
        <div className="space-search">
            <Row className="justify-space-between" style={{ marginTop: "20px", marginBottom: 20 }}>
                <Col lg={10} xl={10}>
                    <div className='manrope f-24 w-700' style={{ display: 'flex', alignItems: "flex-end" }}>
                        Space Library
                    </div>
                </Col>
                <Col lg={14} xl={14} className="justify-in-end">
                    <div className="search-bar-width mr-8">
                        <Form>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <Input 
                                    placeholder="Search All Spaces" 
                                    value={searchValue} 
                                    onChange={e => { changeSearchValue(e.target.value); }} 
                                    className="manrope f-14 mat-search-bar custom-input-id"
                                    style={{ paddingRight: searchValue ? '84px' : '48px' }}
                                    onPressEnter={handleSearchIconClick}
                                />
                                {searchValue && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 42,
                                            top: 3,
                                            height: '87%',
                                            width: '42px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            zIndex: 11,
                                            background: '#fff',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                        }}
                                        onClick={handleClearSearch}
                                    >
                                        <CloseCircleFilled style={{ color: '#aaa', fontSize: 18 }} />
                                    </div>
                                )}
                                <div className="suffix-container" style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#276DD7', borderTopRightRadius: '4px', borderBottomRightRadius: '4px', cursor: 'pointer', zIndex: 10 }} onClick={handleSearchIconClick}>
                                    <ArrowRightOutlined className="custom-icon" />
                                </div>
                            </div>
                        </Form>
                    </div>
                    <div>
                        <Tooltip title={<span className="manrope f-12 white">Apply Filters</span>}>
                            <span>
                                <div className={"icon-button-container mr-8"} onClick={openFilters}>
                                    <FilterOutlined />
                                </div>
                            </span>
                        </Tooltip>
                    </div>
                </Col>
            </Row>
            {getFilteredTags()}
            <RoomsList 
                data={spaces} 
                onLoadMore={handleLoadMore} 
                searchMode={true} 
                selectable={true}
                selected={selectedAdd}
                setSelected={setSelectedAdd}
                setSelectedElementDetials={setSelectedElementDetials}
                selectedRemove={selectedRemove}
                setSelectedRemove={setSelectedRemove}
                artistSearch={artistSearchSpace}
            />

            {/* Bottom menu logic */}
            {selectedAdd.length > 0 || selectedRemove.length > 0 ? (
                <div className="bottom-menu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Selected count on the left */}
                    <div className='manrope black-d9 f-16 ml-168' style={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleFilled className='mr-2' />   {selectedAdd.length > 0 ? `${selectedAdd.length} space(s) selected` : ''} {selectedAdd.length > 0 && selectedRemove.length > 0 ? ', ' : ''} {selectedRemove.length > 0 ? `${selectedRemove.length} space(s) removed` : ''}
                    </div>
                    {/* Loading message in the center if loading more */}
                    {isLoadingMoreData && (
                        <div className='manrope f-16' style={{ color: '#276DD7', padding: 10, display: 'flex', alignItems: 'center' }}>
                            <div className="more-item-loading-container justify-in-center">
                                <Spin indicator={antIcon} tip="Loading more spaces" />
                            </div>
                        </div>
                    )}
                    {/* Button on the right */}
                    <Button
                        className="product-btn approve-button f-14 ml-16 br-4"
                        onClick={handleAddToArtistSearch}
                    >
                        {actionLoader ? <LoadingOutlined /> : ""}
                        Save Changes to Artist Search
                    </Button>
                </div>
            ) : null}
            {/* If no spaces are selected, keep the current loading more message */}
            {selectedAdd.length === 0 && isLoadingMoreData && (
                <div className="more-item-loading-container justify-in-center">
                    <Spin indicator={antIcon} tip="Loading more spaces" />
                </div>
            )}
        </div>}
        <Drawer
                width={360}
                visible={filtersDrawer}
                onClose={closeFiltersDrawer}
                placement="right">
                <div style={{ padding: 20 }}>
                    <h2 className="manrope f-18 black-14 w-700">Filters</h2>
                    <div>
                        <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>
                            Space Category
                        </h5>
                        <Select
                            value={spaceCategoryValue}
                            showSearch={true}
                            className="tree-select-material f-14 filter bg-light"
                            style={{ width: '100%' }}
                            mode="multiple"
                            placeholder="Search and select category"
                            onChange={changeSpaceCategory}
                        >
                            {spaceCategoriesData.map((category) => (
                                <Select.Option
                                    key={category}
                                    className="manrope f-10"
                                    value={category}
                                >
                                    {category}
                                </Select.Option>
                            ))}
                        </Select>


                        {
                            !artistSearchSpace &&
                            <>
                            <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Model Status</h5>
                                <Select
                                    className="tree-select-material f-14 filter bg-light"
                                    value={modelStatus}
                                    onChange={changeModelStatus}
                                    showSearch
                                    placeholder="Search and select model status"
                                    mode="multiple"
                                >
                                    {Object.keys(FileConstants.MODEL_STATUS_STRING).map((key) => (
                                        <Select.Option
                                            key={key}
                                            className="manrope f-10"
                                            value={parseInt(key)}
                                        >
                                            {FileConstants.MODEL_STATUS_STRING[key]}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <h5 className='manrope f-16 black-14' style={{ marginTop: 20 }}>Visible to Artist</h5>
                                <Switch
                                    checked={visibleToArtist}
                                    onChange={setVisibleToArtist}
                                />
                            </>
                        }
                    </div>
                </div>
            </Drawer>
        </>
    )
}

export default SpaceSearch;
